import {Box, Typography} from '@viptech/react-components'
import Progressbar from 'src/components/Progressbar'
import SlideTypography from 'src/components/SlideTypography'
import {FaceDetection} from 'src/services/api'
import {formatDateTime} from 'src/utilities/datetimeUtils'
import {pickStrongerColor} from 'src/utilities/pickStrongerColor'
import PhotoCard from './PhotoCard'
import {useState} from 'react'
import zoomInIcon from 'src/images/zoomInIcon.svg'
import FadeBox from 'src/components/FadeBox'
import zoomOutIcon from 'src/images/zoomOutIcon.svg'

interface Props {
  onClick: (faceDetection: FaceDetection) => void
  faceDetection: FaceDetection
  cameraName?: string
}

export function PhotoEmphasis({faceDetection, cameraName, onClick}: Props) {
  const handleColors = () => {
    let borderSize = '1'
    let borderColor = '#D0D5DD'
    let backgroundColor = '#D0D5DD20'
    let color = '#344054'
    const colors = faceDetection.activatedActions.map((a) => a.actionColor).filter((color): color is string => color !== undefined && color !== null)

    if (colors.length > 0) {
      const strongerColor = pickStrongerColor(colors)
      borderSize = '2'
      borderColor = strongerColor
      backgroundColor = strongerColor + '40'
      color = strongerColor
    }
    const border = `${borderSize}px solid ${borderColor}`

    return {border, backgroundColor, color}
  }

  const [isInDetailedMode, setIsInDetailedMode] = useState(false)
  const [isHoveringPhoto, setIsHoveringPhoto] = useState(false)
  const onMouseEnter = () => setIsHoveringPhoto(true)
  const onMouseLeave = () => setIsHoveringPhoto(false)
  const handleUnfocusPhoto = () => {
    setIsInDetailedMode(false)
    setIsHoveringPhoto(false)
  }

  return (
    <Box
      display='flex'
      direction='column'
      border={handleColors().border}
      backgroundColor={handleColors().backgroundColor}
      padding='8px 16px'
      rowGap='8px'
      style={{cursor: 'pointer'}}
      onClick={() => onClick(faceDetection)}>
      {isInDetailedMode ? (
        <Box width='100%' height='100%' display='flex' direction='column' rowGap='4px'>
          <Box display='flex' justifyContent='space-between' width='100%'>
            <SlideTypography variant='label' size='12px' weight={700} color={handleColors().color}>
              {`${faceDetection.face?.name ?? 'DESCONHECIDO'} em ${cameraName ?? 'DESCONHECIDO'}`}
            </SlideTypography>
          </Box>
          <PhotoCard
            width='100%'
            height='112px'
            src={`${process.env.REACT_APP_EVENT_IMAGES_BASE_URL}/${faceDetection.imageKey}`}
            style={{cursor: 'pointer', aspectRatio: '16/9', position: 'relative'}}
            onClick={(e) => {
              e.stopPropagation()
              handleUnfocusPhoto()
            }}>
            <Box position='relative' width='100%' display='flex' justify='flex-end'>
              <img src={zoomOutIcon} alt='close' width={24} height={20} style={{margin: 8}} color='#433942' />
            </Box>
            <Box position='absolute' bottom='4px' left='4px' width='calc(100% - 8px)'>
              <Box display='flex' justify='space-between' columnGap='4px' width='100%'>
                <Typography
                  variant='label'
                  color='white'
                  size='10px'
                  weight={700}
                  style={{textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000'}}>
                  {formatDateTime(faceDetection.createdAt).formattedDate}
                </Typography>
                <Typography
                  variant='label'
                  color='white'
                  size='10px'
                  weight={700}
                  style={{textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000'}}>
                  {formatDateTime(faceDetection.createdAt).formattedTime}
                </Typography>
              </Box>
            </Box>
          </PhotoCard>
        </Box>
      ) : (
        <Box display='flex' width='100%' direction='column'>
          <Box display='flex' width='100%' justifyContent='space-between'>
            <Box width='91px' display='flex' direction='column' rowGap='4px'>
              <SlideTypography variant='label' size='12px' weight={700} color={handleColors().color}>
                {cameraName ?? 'DESCONHECIDO'}
              </SlideTypography>
              <PhotoCard
                width='100%'
                height='91px'
                showErrorText={false}
                onClick={(e) => {
                  e.stopPropagation()
                  setIsInDetailedMode(true)
                }}
                onMouseEnter={onMouseEnter}
                onMouseLeave={onMouseLeave}
                style={{cursor: 'pointer'}}
                src={`${process.env.REACT_APP_CROPPED_IMAGES_BASE_URL}/${faceDetection.cropImageKey}`}>
                {!isHoveringPhoto && (
                  <Box position='absolute' width={'91px'} display='flex' justify='flex-end'>
                    <img src={zoomInIcon} alt='zoom-in' width={20} height={16} style={{margin: 8}} color='#433942' />
                  </Box>
                )}
                <FadeBox isUnmounting={!isHoveringPhoto} onFadeOutComplete={onMouseLeave}>
                  <Box width='100%' display='flex' justify='center' align='center' height='100%'>
                    <img src={zoomInIcon} alt='zoom-in' width={40} height={42} color='#433942' />
                  </Box>
                </FadeBox>
              </PhotoCard>
            </Box>
            <Box width='91px' display='flex' direction='column' rowGap='4px'>
              <SlideTypography variant='label' size='12px' weight={700} color={handleColors().color}>
                {faceDetection.face?.name ?? 'DESCONHECIDO'}
              </SlideTypography>
              <PhotoCard
                showErrorText={false}
                width='100%'
                height='91px'
                style={{cursor: 'pointer'}}
                src={`${process.env.REACT_APP_TEMPLATE_IMAGES_BASE_URL}/${faceDetection.face?.cropFaceImageKey[0]}`}
              />
            </Box>
          </Box>
        </Box>
      )}

      <Box display='flex' rowGap='4px' direction='column'>
        <Progressbar percentage={Math.round(faceDetection.accuracy * 100)} height='8px' />

        {!isInDetailedMode && (
          <Box display='flex' justify='space-between'>
            <Typography variant='label' size='12px'>
              {formatDateTime(faceDetection.createdAt).formattedDate}
            </Typography>
            <Typography variant='label' size='12px'>
              {formatDateTime(faceDetection.createdAt).formattedTime}
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  )
}
